// @flow

import React from 'react';
import styled from 'styled-components';
import { ForwardArrow, BackArrow } from '../icons';
import { colors, fontWeight } from '../constants';
import { range } from '../utils/range';

type Props = {
  currentPage: number,
  perPage: number,
  numberOfItems: number,
  onPageChange: (number) => void | Promise<void>,
  maxNumberOfPages: number,
  className?: string,
};

class Pagination extends React.Component<Props> {
  static defaultProps = {
    maxNumberOfPages: 5,
  };

  constructor(props: Props) {
    super(props);
  }

  totalNumberOfPages() {
    const { perPage, numberOfItems } = this.props;

    if (perPage <= 0) {
      return 0;
    }

    return Math.ceil(
      Number(numberOfItems.toFixed(2)) / Number(perPage.toFixed(2))
    );
  }

  numberOfVisiblePages() {
    return Math.min(this.props.maxNumberOfPages, this.totalNumberOfPages());
  }

  visiblePages() {
    const { currentPage, maxNumberOfPages } = this.props;
    const numberOfVisiblePages = this.numberOfVisiblePages();
    const totalNumberOfPages = this.totalNumberOfPages();

    if (this.pageOneInVisibleRange()) {
      return range(1, numberOfVisiblePages + 1);
    } else if (this.lastPageInVisibleRange()) {
      return range(
        totalNumberOfPages - numberOfVisiblePages + 1,
        totalNumberOfPages + 1
      );
    }

    const numberOfPagesOnEachSide = Math.floor((maxNumberOfPages - 1) / 2);
    return range(
      currentPage - numberOfPagesOnEachSide,
      currentPage + numberOfPagesOnEachSide + 1
    );
  }

  pageOneInVisibleRange() {
    return this.props.currentPage <= this.numberOfVisiblePages();
  }

  lastPageInVisibleRange() {
    const { currentPage } = this.props;

    return (
      currentPage > this.totalNumberOfPages() - this.numberOfVisiblePages()
    );
  }

  previousPage = () => {
    const { currentPage, onPageChange } = this.props;

    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  nextPage = () => {
    const { currentPage, onPageChange } = this.props;

    if (currentPage < this.totalNumberOfPages()) {
      onPageChange(currentPage + 1);
    }
  };

  renderPreviousArrow() {
    let props = {
      id: 'previous-arrow',
      onClick: this.previousPage,
      title: 'Previous',
      'aria-label': 'Previous',
      hidden: false,
    };

    if (this.props.currentPage === 1) {
      props.hidden = true;
    }

    return (
      <NavArrow {...props}>
        <BackArrow color={colors.mediumGrey} />
      </NavArrow>
    );
  }

  renderNextArrow() {
    let props = {
      id: 'next-arrow',
      onClick: this.nextPage,
      title: 'Next',
      'aria-label': 'Next',
      hidden: false,
    };

    if (this.props.currentPage === this.totalNumberOfPages()) {
      props.hidden = true;
    }

    return (
      <NavArrow {...props}>
        <ForwardArrow color={colors.mediumGrey} />
      </NavArrow>
    );
  }

  renderFirstPage() {
    if (!this.visiblePages().includes(1)) {
      return (
        <span id="first-page">
          {this.renderPageAnchor(1)}
          <Ellipsis>...</Ellipsis>
        </span>
      );
    }
  }

  renderPages() {
    return (
      <ul id="page-list">
        {this.visiblePages().map((pageNumber) => {
          return (
            <li id={'page-' + pageNumber} key={'page-' + pageNumber}>
              {this.renderPageAnchor(pageNumber)}
            </li>
          );
        })}
      </ul>
    );
  }

  renderLastPage() {
    const totalNumberOfPages = this.totalNumberOfPages();

    if (!this.visiblePages().includes(totalNumberOfPages)) {
      return (
        <span id="last-page">
          <Ellipsis>...</Ellipsis>
          {this.renderPageAnchor(totalNumberOfPages)}
        </span>
      );
    }
  }

  renderPageAnchor(pageNumber: number) {
    return (
      <PageAnchor {...this.pageAnchorProps(pageNumber)}>
        {pageNumber}
      </PageAnchor>
    );
  }

  pageAnchorProps(pageNumber: number) {
    const { currentPage, onPageChange } = this.props;

    let props = {
      href: '#',
      role: 'button',
      onClick: () => onPageChange(pageNumber),
      'aria-disabled': 'false',
      disabled: false,
    };

    if (pageNumber === currentPage) {
      props.disabled = true;
      props['aria-disabled'] = 'true';
    }

    return props;
  }

  render() {
    return (
      <Container className={this.props.className}>
        {this.renderPreviousArrow()}
        {this.renderFirstPage()}
        {this.renderPages()}
        {this.renderLastPage()}
        {this.renderNextArrow()}
      </Container>
    );
  }
}

const Container = styled.div`
  text-align: right;
  font-size: 14px;
  user-select: none;

  ${fontWeight('normal')}
  box-sizing: border-box;

  ul {
    display: inline;

    li {
      display: inline;
    }
  }

  a {
    display: inline-block;
    width: 32px;
    height: 32px;

    &:hover {
      border-radius: 3px;
      text-decoration: none;
    }
  }
`;
const PageAnchor = styled.a`
  cursor: pointer;
  padding: 6px 0px;
  color: ${colors.darkGrey};
  text-align: center;

  &:hover {
    background-color: ${colors.lightGrey};
  }

  ${(props) =>
    props.disabled &&
    `
      cursor: not-allowed;
      color: ${colors.white};
      background-color: ${colors.mediumGreenhouseGreen};
      border-radius: 3px;

      &:hover {
        background-color: ${colors.mediumGreenhouseGreen};
      }
    `}
`;
const NavArrow = styled.a`
  cursor: pointer;
  padding: 7px 0px;
  text-align: center;
  position: relative;
  top: 5px;

  &:hover {
    background-color: ${colors.lightGrey};
  }

  ${(props) => props.hidden && `visibility: hidden;`}
`;

const Ellipsis = styled.span`
  position: relative;
  bottom: -1px;
  letter-spacing: 1px;
`;

export default Pagination;
